import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, coerceBooleanFromString } from '../schemas';

const projectIdParamsSchema = z.object({
	projectId: requiredStringSchema,
});

/**
 * Projects storage contract - routes under /projects prefix.
 *
 * IMPORTANT: Specific paths must come BEFORE parameterized paths.
 */
export const projectsContract = defineContract('projects', {
	createProjectBucket: route({
		method: 'POST',
		path: '/:projectId/bucket',
		params: projectIdParamsSchema,
		response: z.string(),
		operationId: 'createProjectBucket',
		tags: ['Projects Storage'],
		summary: 'Create a project S3 bucket',
	}),
	deleteProjectBucket: route({
		method: 'DELETE',
		path: '/:projectId/bucket',
		params: projectIdParamsSchema,
		response: z.string(),
		operationId: 'deleteProjectBucket',
		tags: ['Projects Storage'],
		summary: 'Delete a project S3 bucket',
	}),
	getBuildingView: route({
		method: 'GET',
		path: '/:projectId/buildingView',
		params: projectIdParamsSchema,
		response: z.any(),
		operationId: 'getBuildingView',
		tags: ['Projects Storage'],
		summary: 'Get building SVG for a project',
	}),
	uploadBuildingView: route({
		method: 'POST',
		path: '/:projectId/buildingView',
		contentType: 'multipart/form-data',
		params: projectIdParamsSchema,
		body: z.object({
			file: z.file(),
		}),
		response: z.string(),
		operationId: 'uploadBuildingViewProject',
		tags: ['Projects Storage'],
		summary: 'Upload building SVG for a project',
	}),
	getFloorViewRaw: route({
		method: 'GET',
		path: '/:projectId/floorView/raw',
		params: projectIdParamsSchema,
		query: z.object({ floorId: requiredStringSchema }),
		response: z.any(),
		operationId: 'getFloorViewRaw',
		tags: ['Projects Storage'],
		summary: 'Get raw floor image',
	}),
	getFloorView: route({
		method: 'GET',
		path: '/:projectId/floorView',
		params: projectIdParamsSchema,
		query: z.object({ floorId: requiredStringSchema }),
		response: z.any(),
		operationId: 'getFloorView',
		tags: ['Projects Storage'],
		summary: 'Get floor SVG view',
	}),
	uploadFloorViewRaw: route({
		method: 'POST',
		path: '/:projectId/floorView/raw',
		contentType: 'multipart/form-data',
		params: projectIdParamsSchema,
		body: z.object({
			file: z.preprocess((val) => (Array.isArray(val) ? val : [val]), z.array(z.file())),
		}),
		response: z.string(),
		operationId: 'uploadFloorViewRaw',
		tags: ['Projects Storage'],
		summary: 'Upload raw floor images',
	}),
	uploadFloorView: route({
		method: 'POST',
		path: '/:projectId/floorView',
		contentType: 'multipart/form-data',
		params: projectIdParamsSchema,
		body: z.object({
			file: z.preprocess((val) => (Array.isArray(val) ? val : [val]), z.array(z.file())),
		}),
		response: z.string(),
		operationId: 'uploadFloorView',
		tags: ['Projects Storage'],
		summary: 'Upload floor SVG views',
	}),
	uploadProjectLogo: route({
		method: 'POST',
		path: '/:projectId/logo',
		contentType: 'multipart/form-data',
		params: projectIdParamsSchema,
		body: z.object({
			file: z.file(),
		}),
		response: z.string(),
		operationId: 'uploadProjectLogo',
		tags: ['Projects Storage'],
		summary: 'Upload project logo',
	}),
	getProjectLogo: route({
		method: 'GET',
		path: '/:projectId/logo',
		params: projectIdParamsSchema,
		query: z.object({ isMobile: coerceBooleanFromString.optional() }),
		response: z.any(),
		operationId: 'getProjectLogo',
		tags: ['Projects Storage'],
		summary: 'Get project logo',
	}),
	getProjectImage: route({
		method: 'GET',
		path: '/:projectId/projectImage',
		params: projectIdParamsSchema,
		response: z.any(),
		operationId: 'getProjectImage',
		tags: ['Projects Storage'],
		summary: 'Get project image as base64',
	}),
	uploadProjectImage: route({
		method: 'POST',
		path: '/:projectId/projectImage',
		contentType: 'multipart/form-data',
		params: projectIdParamsSchema,
		body: z.object({
			file: z.file(),
		}),
		response: z.string(),
		operationId: 'uploadProjectImage',
		tags: ['Projects Storage'],
		summary: 'Upload project image',
	}),
	copyBucket: route({
		method: 'POST',
		path: '/:projectId/copyBucket',
		params: projectIdParamsSchema,
		query: z.object({ environment: z.string().optional() }),
		response: z.string(),
		operationId: 'copyBucket',
		tags: ['Projects Storage'],
		summary: 'Copy bucket contents to another environment',
	}),
});
